import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { getBookingDetails, checkIn, checkOut, updateBookingGuestDetails } from "../../../../api/services/guestService";
import { getKeywordBasedSearch } from "../../../../api/services/hostService";
import RazorpayCheckout from "../../../../components/Payment/RazorpayCheckout";
import { useUserContext, createRazorpayOrder } from "../../../protectedRoute/UserContext";
import LandingHeader from "../../../../components/landing/Header";
import LandingPageHeader from "../../../../components/LandingPageHeader/LandingPageHeader";
import Footer from "../../../../components/landing/Footer";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Separator } from "../../../../components/ui/separator";
import { ArrowLeft, Shield, Calendar, Users, Check, X, MapPin, Star } from "lucide-react";
import { useToast } from "../../../../hooks/use-toast";
import { z } from "zod";
import { Checkbox } from "../../../../components/ui/checkbox";

interface BookingConfirmationResponse {
  id: string; // Changed from booking_id: number
  total_price: number;
  gst_amount: number;
  price_with_gst: number;
  message: string;
  check_in_status: boolean;
  check_out_status: boolean;
  check_in: string;
  check_out: string;
  number_of_guests: number;
  property_id: string; // Added property_id
  status: string; // Added status
  guest: string; // Added guest
}

const guestSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(50, "First name too long"),
  lastName: z.string().trim().min(1, "Last name is required").max(50, "Last name too long"),
  email: z.string().trim().email("Invalid email address").max(100, "Email too long"),
  phone: z.string().trim().min(10, "Phone number must be at least 10 digits").max(15, "Phone number too long"),
});

type GuestDetails = z.infer<typeof guestSchema>;

const BookingConfirmation: React.FC = () => {
  const { booking_id } = useParams<{ booking_id: string }>();
  const navigate = useNavigate();
  const { state, dispatch } = useUserContext();
  const { paymentSuccess, paymentError, user } = state;
  const { toast } = useToast();

  const [bookingDetails, setBookingDetails] = useState<BookingConfirmationResponse | null>(null);
  const [propertyDetails, setPropertyDetails] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const [showRazorpay, setShowRazorpay] = useState(false);

  const [guestDetails, setGuestDetails] = useState<GuestDetails>({
    firstName: user?.first_name || "",
    lastName: user?.last_name || "",
    email: user?.email || "",
    phone: user?.phone_number || "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof GuestDetails, string>>>({});
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [specialRequests, setSpecialRequests] = useState("");

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const bookingResponse = await getBookingDetails(booking_id!);
        setBookingDetails(bookingResponse);
        console.log('bookingResponse:', bookingResponse);
        if (bookingResponse.property_id) {
          const propertyResponse = await getKeywordBasedSearch({
            property_id: bookingResponse.property_id,
            keyword: '',
            checkin: bookingResponse.check_in,
            checkout: bookingResponse.check_out,
            accommodation_type: ''
          });
          setPropertyDetails(propertyResponse);
          console.log('propertyDetails:', propertyResponse);
        }
      } catch (error) {
        setError("Failed to fetch booking details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [booking_id]);

  const handleInputChange = (field: keyof GuestDetails, value: string) => {
    setGuestDetails(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    if (user) return true; // Skip validation if user is logged in
    try {
      guestSchema.parse(guestDetails);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Partial<Record<keyof GuestDetails, string>> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as keyof GuestDetails] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handlePayNow = async () => {
    console.log("BookingConfirmation: handlePayNow called");
    if (!validateForm()) {
      toast({
        title: "Please fix the errors",
        description: "Some required fields are missing or invalid.",
        variant: "destructive",
      });
      return;
    }

    if (!agreeTerms) {
      toast({
        title: "Terms & Conditions",
        description: "Please agree to the terms and conditions to proceed.",
        variant: "destructive",
      });
      return;
    }

    try {
      if (!user) {
        await updateBookingGuestDetails(booking_id!, {
          first_name: guestDetails.firstName,
          last_name: guestDetails.lastName,
          email: guestDetails.email,
          phone_number: guestDetails.phone,
        });
      }
      if (!bookingDetails || !bookingDetails.id) {
        toast({
          title: "Payment Error",
          description: "Booking details not available. Please refresh and try again.",
          variant: "destructive",
        });
        return;
      }
      await createRazorpayOrder(dispatch, booking_id!);
      setShowRazorpay(true);
    } catch (error: any) {
      toast({
        title: "Payment Error",
        description: "Failed to initiate payment. Please try again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (paymentSuccess) {
      navigate(`/new-payment-success/${booking_id}`);
      dispatch({ type: "RESET_PAYMENT_STATE" });
    }
    if (paymentError) {
      navigate(`/new-payment-failure/${booking_id}`);
      dispatch({ type: "RESET_PAYMENT_STATE" });
    }
  }, [paymentSuccess, paymentError, navigate, booking_id, dispatch]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-deep-emerald-green"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto mt-8 text-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </div>
    );
  }

  if (!bookingDetails) {
    return (
      <div className="container mx-auto mt-8 text-center">
        <p className="text-lg font-semibold">No booking details found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {state.role === 'guest' ? <LandingHeader /> : <LandingPageHeader isMobileMenuOpen={false} setIsMobileMenuOpen={() => {}} />}
      
      <main className="container mx-auto px-4 py-8">
        <Button variant="ghost" className="mb-6" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <h1 className="text-3xl font-bold">Confirm Your Booking</h1>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Guest Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input id="firstName" placeholder="John" value={guestDetails.firstName} onChange={(e) => handleInputChange("firstName", e.target.value)} className={errors.firstName ? "border-destructive" : ""} readOnly={!!user} />
                    {errors.firstName && <p className="text-sm text-destructive">{errors.firstName}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input id="lastName" placeholder="Doe" value={guestDetails.lastName} onChange={(e) => handleInputChange("lastName", e.target.value)} className={errors.lastName ? "border-destructive" : ""} readOnly={!!user} />
                    {errors.lastName && <p className="text-sm text-destructive">{errors.lastName}</p>}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input id="email" type="email" placeholder="john@example.com" value={guestDetails.email} onChange={(e) => handleInputChange("email", e.target.value)} className={errors.email ? "border-destructive" : ""} readOnly={!!user} />
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input id="phone" type="tel" placeholder="+91 9876543210" value={guestDetails.phone} onChange={(e) => handleInputChange("phone", e.target.value)} className={errors.phone ? "border-destructive" : ""} readOnly={!!user} />
                    {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="specialRequests">Special Requests (Optional)</Label>
                  <textarea
                    id="specialRequests"
                    className="w-full min-h-[100px] px-3 py-2 border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Any special requirements, late check-in, dietary preferences, etc."
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    maxLength={500}
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {specialRequests.length}/500
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={agreeTerms}
                    onCheckedChange={(checked: boolean) => setAgreeTerms(checked)}
                  />
                  <label htmlFor="terms" className="text-sm text-muted-foreground cursor-pointer">
                    I agree to the{" "}
                    <a href="#" className="text-primary hover:underline">Terms of Service</a>,{" "}
                    <a href="#" className="text-primary hover:underline">Privacy Policy</a>, and{" "}
                    <a href="#" className="text-primary hover:underline">Cancellation Policy</a>.
                    I understand that my booking is subject to the property's house rules.
                  </label>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                  <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Secure Payment via Razorpay</p>
                    <p className="text-xs text-muted-foreground">
                      Pay securely using UPI, Credit/Debit Cards, Net Banking, or Wallets.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card>
                <CardHeader>
                  <CardTitle>Booking Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {propertyDetails && (
                    <div className="flex gap-4">
                      <img
                        src={propertyDetails.images?.[0]?.image}
                        alt={propertyDetails.name}
                        className="w-24 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold line-clamp-1">{propertyDetails.name}</h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {propertyDetails.location?.street_address}, {propertyDetails.location?.city}
                        </p>
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="text-sm font-medium">{propertyDetails.average_rating}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <Separator />
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">Check-in:</span>
                      <span>{new Date(bookingDetails.check_in).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">Check-out:</span>
                      <span>{new Date(bookingDetails.check_out).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <Separator />
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{bookingDetails.number_of_guests} Guest(s)</span>
                  </div>
                  <Separator />
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Accommodation</span>
                      <span>₹{Number(bookingDetails.total_price).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Reserve (10%)</span>
                      <span>₹{(Number(bookingDetails.price_with_gst) - Number(bookingDetails.gst_amount)).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>GST on Reserve (18%)</span>
                      <span>₹{Number(bookingDetails.gst_amount).toLocaleString()}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between font-bold text-base">
                      <span>Pay online now</span>
                      <span>₹{Number(bookingDetails.price_with_gst).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground text-xs">
                      <span>Pay host on arrival</span>
                      <span>₹{Number(bookingDetails.total_price).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-foreground pt-1 border-t border-border">
                      <span>Total trip cost</span>
                      <span>₹{(Number(bookingDetails.total_price) + Number(bookingDetails.price_with_gst)).toLocaleString()}</span>
                    </div>
                  </div>

                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handlePayNow}
                    disabled={!agreeTerms}
                  >
                    {`Reserve · Pay ₹${Number(bookingDetails.price_with_gst).toLocaleString()}`}
                  </Button>

                  {showRazorpay && bookingDetails && bookingDetails.id && (
                    console.log("BookingConfirmation: Rendering RazorpayCheckout component"),
                    <RazorpayCheckout
                      bookingId={bookingDetails.id.toString()}
                      firstName={guestDetails.firstName}
                      lastName={guestDetails.lastName}
                      email={guestDetails.email}
                      phoneNumber={guestDetails.phone}
                    />
                  )}

                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BookingConfirmation;
